import React, { useState, useEffect } from 'react';
import { IQuery, IUser } from '@eagerdog/interfaces';
import { Constants } from '@eagerdog/constants';
import { useDebounce } from '../../hooks/useDebounce';
import Button from '../Button/Button';
import Input from '../Input/Input';
import Toggle from '../Toggle/Toggle';
import { Modal } from '../Modal/Modal';
import { apiService } from '../../services/api.service';
import { toast } from '../Toast/ToastManager';

import './AddContactModal.scss';

interface IAddContactModalProps {
  isShown: boolean;
  hide: () => void;
  clubId: string;
  onSuccess: () => void;
}

export const AddContactModal: React.FC<IAddContactModalProps> = ({
  isShown,
  hide,
  clubId,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  
  // User search states
  const [emailSearch, setEmailSearch] = useState('');
  const [userList, setUserList] = useState<IUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  
  // Existing contacts (to filter out)
  const [existingContactIds, setExistingContactIds] = useState<Set<string>>(new Set());
  
  // Capacity toggles
  const [hasParticipant, setHasParticipant] = useState<boolean>(false);
  const [hasVolunteer, setHasVolunteer] = useState<boolean>(false);
  const [hasMember, setHasMember] = useState<boolean>(false);
  const [hasAdmin, setHasAdmin] = useState<boolean>(false);
  
  const dEmailSearch = useDebounce<string>(emailSearch, 800);

  // Fetch existing contacts when modal opens
  const fetchExistingContacts = async () => {
    try {
      const response = await apiService.getClubCapacities(clubId);
      const ids = new Set<string>();
      for (const capacity of response.capacities) {
        ids.add(capacity.user_id);
      }
      setExistingContactIds(ids);
    } catch (error) {
      console.error('Error fetching existing contacts:', error);
    }
  };

  // Search users function
  const searchUsers = async (userEmail: string) => {
    if (!userEmail.trim()) {
      setUserList([]);
      return;
    }

    setSearchLoading(true);

    const query: IQuery = {
      $limit: 5,
      sort: [{
        attribute_name: "email",
        sort: "asc"
      }],
      query: {
        $and: [
          {
            attribute_name: "email",
            attribute_value: {
              operator: "$regex",
              value: userEmail
            }
          },
          {
            attribute_name: "user_type",
            attribute_value: {
              operator: "$eq",
              value: "owner"
            }
          }
        ]
      }
    };

    try {
      const response = await apiService.getUsers(query);
      setUserList(response);
    } catch (error) {
      console.error('Error searching users:', error);
      setUserList([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle user selection
  const handleUserSelect = (user: IUser) => {
    setSelectedUser(user);
    setEmailSearch(user.email || '');
    setUserList([]);
    setErrors({});
  };

  // Effect for debounced search
  useEffect(() => {
    if (dEmailSearch && dEmailSearch.length > 0 && !selectedUser) {
      searchUsers(dEmailSearch);
    } else if (!dEmailSearch || dEmailSearch.length === 0) {
      setUserList([]);
    }
  }, [dEmailSearch, selectedUser]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isShown) {
      setEmailSearch('');
      setUserList([]);
      setSelectedUser(null);
      setErrors({});
      setSearchLoading(false);
      // Reset toggles
      setHasParticipant(false);
      setHasVolunteer(false);
      setHasMember(false);
      setHasAdmin(false);
      // Fetch existing contacts
      fetchExistingContacts();
    }
  }, [isShown]);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!selectedUser) {
      newErrors.user = 'Please select a user from the search results';
      return false;
    }

    // At least one capacity must be selected
    if (!hasParticipant && !hasVolunteer && !hasMember && !hasAdmin) {
      newErrors.capacities = 'Please select at least one role';
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !selectedUser) {
      return;
    }

    setLoading(true);
    try {
      // Build list of capacities to add
      const capacitiesToAdd: string[] = [];
      if (hasParticipant) capacitiesToAdd.push(Constants.capacity_type.participant);
      if (hasVolunteer) capacitiesToAdd.push(Constants.capacity_type.volunteer);
      if (hasMember) capacitiesToAdd.push(Constants.capacity_type.member);
      if (hasAdmin) capacitiesToAdd.push(Constants.capacity_type.admin);

      // Add capacities sequentially to avoid race condition with duplicate key
      for (const capacity of capacitiesToAdd) {
        await apiService.addClubCapacity(clubId, selectedUser._id.toString(), capacity);
      }

      toast.show({
        title: 'Success',
        content: `Contact added successfully with ${capacitiesToAdd.length} role(s)`,
        type: 'success',
        duration: 3000
      });

      // Call onSuccess first to refresh data, then close
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error adding contact:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to add contact. Please try again.';
      
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmailSearch('');
    setUserList([]);
    setSelectedUser(null);
    setErrors({});
    setHasParticipant(false);
    setHasVolunteer(false);
    setHasMember(false);
    setHasAdmin(false);
    hide();
  };

  const modalContent = (
    <div className="addContactModal">
      <div className="modalHeader">
        <h2>Add Contact</h2>
        <p className="description">Add a new contact to the club and assign their roles.</p>
      </div>

      <div className="scrollableContent">
        {!selectedUser ? (
          <div className="userSearchSection">
            <div className="formGroup">
              <label htmlFor="emailSearch">Search by Email Address *</label>
              <Input
                id="emailSearch"
                type="email"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                placeholder="Type email to search for existing users..."
                autoComplete="off"
              />
            </div>

            {searchLoading && (
              <div className="searchLoading">
                <div className="loadingSpinner"></div>
                <span>Searching users...</span>
              </div>
            )}

            {!searchLoading && emailSearch.length > 0 && userList.length === 0 && (
              <div className="noUserFound">
                <span>No users found matching "{emailSearch}"</span>
              </div>
            )}

            {!searchLoading && userList.length > 0 && (
              <ul className="usersFound">
                {userList.map((user: IUser, index: number) => {
                  const isExistingContact = existingContactIds.has(user._id.toString());
                  return (
                    <li 
                      key={index} 
                      onClick={() => !isExistingContact && handleUserSelect(user)}
                      className={isExistingContact ? 'disabled' : ''}
                    >
                      <div className="userInfo">
                        <div className="userName">
                          {user.first_name || ''} {user.last_name || ''}
                          {isExistingContact && <span className="existingBadge">Already a contact</span>}
                        </div>
                        <div className="userEmail">{user.email || ''}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {errors.user && <div className="fieldError">{errors.user}</div>}
          </div>
        ) : (
          <>
            <div className="selectedUserSection">
              <div className="selectedUserInfo">
                <h3>Selected User:</h3>
                <div className="userDetails">
                  <div className="userName">{selectedUser.first_name || ''} {selectedUser.last_name || ''}</div>
                  <div className="userEmail">{selectedUser.email || ''}</div>
                </div>
                <button 
                  type="button" 
                  className="changeUserBtn"
                  onClick={() => {
                    setSelectedUser(null);
                    setEmailSearch('');
                    setUserList([]);
                  }}
                >
                  Change User
                </button>
              </div>
            </div>

            <div className="capacitiesSection">
              <h3>Select Roles</h3>
              
              <div className="capacityItem">
                <div className="capacityInfo">
                  <label className="capacityLabel">Participant</label>
                  <p className="capacityDescription">
                    Can participate in events and register dogs
                  </p>
                </div>
                <div className="capacityControl">
                  <Toggle
                    id="add-capacity-participant"
                    checked={hasParticipant}
                    onChange={(checked) => setHasParticipant(checked)}
                  />
                </div>
              </div>

              <div className="capacityItem">
                <div className="capacityInfo">
                  <label className="capacityLabel">Volunteer</label>
                  <p className="capacityDescription">
                    Can access volunteer features and early/late registration (if enabled)
                  </p>
                </div>
                <div className="capacityControl">
                  <Toggle
                    id="add-capacity-volunteer"
                    checked={hasVolunteer}
                    onChange={(checked) => setHasVolunteer(checked)}
                  />
                </div>
              </div>

              <div className="capacityItem">
                <div className="capacityInfo">
                  <label className="capacityLabel">Member</label>
                  <p className="capacityDescription">
                    Club member with access to member features
                  </p>
                </div>
                <div className="capacityControl">
                  <Toggle
                    id="add-capacity-member"
                    checked={hasMember}
                    onChange={(checked) => setHasMember(checked)}
                  />
                </div>
              </div>

              <div className="capacityItem">
                <div className="capacityInfo">
                  <label className="capacityLabel">Admin</label>
                  <p className="capacityDescription">
                    Full administrative access to manage the club and events
                  </p>
                </div>
                <div className="capacityControl">
                  <Toggle
                    id="add-capacity-admin"
                    checked={hasAdmin}
                    onChange={(checked) => setHasAdmin(checked)}
                  />
                </div>
              </div>

              {errors.capacities && <div className="fieldError">{errors.capacities}</div>}
            </div>
          </>
        )}
      </div>

      <form onSubmit={handleSubmit} className="modalForm">
        {errors.submit && (
          <div className="error submitError">
            {errors.submit}
          </div>
        )}

        <div className="modalActions">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            disabled={loading || !selectedUser}
          >
            {loading ? 'Adding...' : 'Add Contact'}
          </Button>
        </div>
      </form>
    </div>
  );

  return (
    <Modal 
      isShown={isShown} 
      hide={handleClose} 
      className="addContactModalWrapper"
      modalContent={modalContent}
    />
  );
};

export default AddContactModal;

